import { AutoStack } from 'components'
import { FormikDepartmentPicker } from 'components/DepartmentDropdown/DepartmentDropdown'
import { FieldArray } from 'formik'
import { FormikDropdown } from 'formik-office-ui-fabric-react'
import { FieldStack, FieldStackItem, FormStack } from 'forms/FormStack'
import { ParticipantInsertInput } from 'model'
import { IDropdownOption, Label, Text, useTheme } from 'office-ui-fabric-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

export interface ProvingDetailsWorkerFormValues {
  participants: ParticipantInsertInput[]
}

export class ProvingDetailsWorkerFormSchema {
  constructor() {
    return Yup.object().shape({
      participants: Yup.array().of(
        Yup.object().shape({
          unknownPerson: Yup.object().shape({
            department: Yup.string().required(),
          }),
          orderId: Yup.string().required('Auftragsnummer ist ein Pflichtfeld'),
        })
      ),
    })
  }
}

export const ProvingDetailsWorkerForm: React.FC<{
  orderIds: string[]
}> = ({ orderIds }) => {
  const { t } = useTranslation()
  const theme = useTheme()

  const percentages: IDropdownOption[] = [
    { key: 100, text: '100%' },
    { key: 90, text: '90%' },
    { key: 80, text: '80%' },
    { key: 70, text: '70%' },
    { key: 60, text: '60%' },
    { key: 50, text: '50%' },
    { key: 40, text: '40%' },
    { key: 30, text: '30%' },
    { key: 20, text: '20%' },
    { key: 10, text: '10%' },
  ]
  const workerCountToAdd: IDropdownOption[] = [
    { key: 1, text: '1' },
    { key: 2, text: '2' },
    { key: 3, text: '3' },
    { key: 4, text: '4' },
    { key: 5, text: '5' },
    { key: 6, text: '6' },
    { key: 7, text: '7' },
    { key: 8, text: '8' },
    { key: 9, text: '9' },
    { key: 10, text: '10' },
  ]

  return (
    <FormStack>
      <Label>{t('field.maintenanceArea.header')}</Label>

      <>
        <FieldArray name="participants">
          {({ form, name, ...helpers }) => {
            const values: {
              percentage: number
              orderId: string
            }[] = form.values[name]

            return (
              <AutoStack tokens={{ childrenGap: 12 }}>
                <FieldStack horizontal={true}>
                  <Text styles={{ root: { color: theme.palette.red } }}>
                    Bitte stichwortartig die Kompetenz der Werkstatt sowie grobe
                    Aufgaben während der Erprobung im Anmerkungsfeld angeben
                  </Text>
                </FieldStack>
                <FieldStack>
                  <FieldStackItem
                    name={`numberOfWorkers`}
                    render={(fieldProps) => {
                      return (
                        <FormikDropdown
                          {...fieldProps}
                          required={true}
                          label={'Anzahl'}
                          options={workerCountToAdd}
                          onChanged={(option: { key: any }) => {
                            if (option.key) {
                              const workerCounter = Number(option.key)
                              if (workerCounter > values.length) {
                                for (
                                  let i = 0;
                                  i < workerCounter - values.length;
                                  i++
                                ) {
                                  helpers.push({
                                    percentage: 100,
                                    orderId: '',
                                  })
                                }
                              }
                              if (workerCounter < values.length) {
                                for (
                                  let j = values.length - 1;
                                  j > workerCounter - 1;
                                  j--
                                ) {
                                  helpers.remove(j)
                                }
                              }
                            }
                          }}
                        />
                      )
                    }}
                  />
                </FieldStack>

                {values.map((x: any, i: number) => (
                  <FieldStack horizontal={true} key={i}>
                    <FieldStack>
                      <FieldStackItem
                        name={`participants[${i}].unknownPerson.department`}
                        render={(fieldProps) => {
                          return (
                            <FormikDepartmentPicker
                              {...fieldProps}
                              required={true}
                              label={t('field.maintenanceArea.label')}
                            />
                          )
                        }}
                      />
                    </FieldStack>
                    <FieldStackItem
                      name={`participants[${i}].percentage`}
                      isRequired={true}
                      render={(fieldProps) => (
                        <FormikDropdown
                          label={t('field.percentage.label')}
                          defaultSelectedKey={100}
                          {...fieldProps}
                          options={percentages}
                        />
                      )}
                    />
                    <FieldStackItem
                      name={`participants[${i}].orderId`}
                      isRequired={true}
                      render={(fieldProps) => (
                        <FormikDropdown
                          label={t('field.orderId.label')}
                          {...fieldProps}
                          required={true}
                          options={[
                            ...orderIds.map(
                              (orderId) =>
                                ({
                                  key: orderId,
                                  text: orderId,
                                } as IDropdownOption)
                            ),
                          ]}
                        />
                      )}
                    />
                  </FieldStack>
                ))}
              </AutoStack>
            )
          }}
        </FieldArray>
      </>
    </FormStack>
  )
}
